import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

/* ------------------------------------------------------------------ */
/*  Keyframe animations (injected once)                                */
/* ------------------------------------------------------------------ */

const animationCSS = `
@keyframes float-slow {
  0%, 100% { transform: translateY(0) rotate(var(--start-rot, 0deg)); }
  50% { transform: translateY(-18px) rotate(calc(var(--start-rot, 0deg) + 6deg)); }
}
@keyframes float-medium {
  0%, 100% { transform: translateY(0) rotate(var(--start-rot, 0deg)); }
  50% { transform: translateY(-12px) rotate(calc(var(--start-rot, 0deg) - 5deg)); }
}
@keyframes fade-in-up {
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}
.login-float-slow { animation: float-slow 7s ease-in-out infinite; }
.login-float-medium { animation: float-medium 5s ease-in-out infinite; }
.login-fade-in { animation: fade-in-up 0.6s ease-out both; }
.login-fade-in-d1 { animation: fade-in-up 0.6s ease-out 0.1s both; }
.login-fade-in-d2 { animation: fade-in-up 0.6s ease-out 0.2s both; }
.login-fade-in-d3 { animation: fade-in-up 0.6s ease-out 0.3s both; }
.login-fade-in-d4 { animation: fade-in-up 0.6s ease-out 0.4s both; }
`;

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

type ViewMode = 'login' | 'signup' | 'verify';

const LoginPage: React.FC = () => {
  const { login, loginWithGoogle, signup, confirmSignUp, completeNewPassword, needsPasswordChange, authError, clearAuthError } = useAuth();
  const [viewMode, setViewMode] = useState<ViewMode>('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupConfirmPassword, setSignupConfirmPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const displayError = authError || error;

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    clearAuthError();
    setLoading(true);
    try {
      await login(username, password);
    } catch (err: any) {
      setError(`Login failed: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    clearAuthError();
    if (signupPassword !== signupConfirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (signupPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      await signup(signupEmail, signupPassword);
      setViewMode('verify');
    } catch (err: any) {
      setError(err.message || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    clearAuthError();
    setLoading(true);
    try {
      await confirmSignUp(signupEmail, verificationCode);
      // After verification, log them in
      await login(signupEmail, signupPassword);
    } catch (err: any) {
      setError(err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const switchToSignup = () => { setError(''); clearAuthError(); setViewMode('signup'); };
  const switchToLogin = () => { setError(''); clearAuthError(); setViewMode('login'); };

  const handleGoogleLogin = async () => {
    setError('');
    clearAuthError();
    setLoading(true);
    try {
      await loginWithGoogle();
    } catch (err: any) {
      setError(`Google login failed: ${err.message || 'Unknown error'}`);
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    setLoading(true);
    try {
      await completeNewPassword(newPassword);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /* ---- shared pieces ---- */

  const inputClass =
    'w-full px-4 py-3 rounded-xl border-2 border-amber-200/60 bg-amber-50/40 ' +
    'text-heading placeholder-amber-400/70 ' +
    'focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-300/40 ' +
    'transition-all duration-200 text-sm';

  const primaryBtnClass =
    'w-full py-3 px-4 rounded-xl font-semibold text-white text-sm ' +
    'bg-gradient-to-r from-amber-500 to-orange-500 ' +
    'hover:from-amber-600 hover:to-orange-600 ' +
    'focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 ' +
    'disabled:opacity-50 disabled:cursor-not-allowed ' +
    'transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 ' +
    'shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40';

  const errorBox = displayError || error ? (
    <div className="rounded-xl bg-red-50 border border-red-200 p-3 login-fade-in">
      <p className="text-sm text-red-700 text-center">{displayError || error}</p>
    </div>
  ) : null;

  /* ---- background shell ---- */
  const shell = (children: React.ReactNode) => (
    <>
      <style>{animationCSS}</style>
      <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
        {/* full-screen background image */}
        <img
          src="/images/landing_page.png"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        {/* dark overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/70" />

        {/* card */}
        <div className="relative z-10 w-full max-w-md mx-4">
          {children}
        </div>
      </div>
    </>
  );

  /* ---------------------------------------------------------------- */
  /*  Password change view                                             */
  /* ---------------------------------------------------------------- */

  if (needsPasswordChange) {
    return shell(
      <div className="rounded-2xl p-8 sm:p-10 login-fade-in"
        style={{
          background: 'linear-gradient(135deg, rgba(255,251,235,0.97) 0%, rgba(255,247,237,0.95) 100%)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 25px 60px rgba(0,0,0,0.3), 0 0 0 1px rgba(245,158,11,0.15)',
        }}
      >
        {/* icon */}
        <div className="flex justify-center mb-5">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/30">
            <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
            </svg>
          </div>
        </div>

        <h2 className="text-center text-2xl font-bold text-heading mb-1">
          Set New Password
        </h2>
        <p className="text-center text-sm text-amber-700/70 mb-8">
          Choose a secure password for your account
        </p>

        <form className="space-y-4" onSubmit={handlePasswordChange}>
          <div>
            <label htmlFor="new-password" className="sr-only">New Password</label>
            <input
              id="new-password"
              name="new-password"
              type="password"
              autoComplete="new-password"
              required
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className={inputClass}
              placeholder="New password (min 8 chars)"
              minLength={8}
            />
          </div>
          <div>
            <label htmlFor="confirm-password" className="sr-only">Confirm Password</label>
            <input
              id="confirm-password"
              name="confirm-password"
              type="password"
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={inputClass}
              placeholder="Confirm new password"
              minLength={8}
            />
          </div>

          {errorBox}

          <button type="submit" disabled={loading} className={primaryBtnClass}>
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                Setting password...
              </span>
            ) : 'Set New Password'}
          </button>
        </form>
      </div>
    );
  }

  /* ---------------------------------------------------------------- */
  /*  Shared card header                                               */
  /* ---------------------------------------------------------------- */

  const logoHeader = (subtitle: string) => (
    <div className="text-center mb-8 login-fade-in-d1">
      <div className="flex justify-center mb-4">
        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-amber-400 via-orange-500 to-rose-500 flex items-center justify-center shadow-lg shadow-orange-500/30 transform -rotate-3">
          <svg className="w-10 h-10 text-white" viewBox="0 0 64 64" fill="none">
            <rect x="8" y="8" width="48" height="48" rx="8" stroke="currentColor" strokeWidth="3" />
            <circle cx="22" cy="22" r="4" fill="currentColor" />
            <circle cx="42" cy="22" r="4" fill="currentColor" />
            <circle cx="32" cy="32" r="4" fill="currentColor" />
            <circle cx="22" cy="42" r="4" fill="currentColor" />
            <circle cx="42" cy="42" r="4" fill="currentColor" />
          </svg>
        </div>
      </div>
      <h1 className="text-2xl font-bold text-heading tracking-tight">
        Board Game Stats
      </h1>
      <p className="text-sm text-amber-700/60 mt-1">{subtitle}</p>
    </div>
  );

  const googleButton = (
    <div className="login-fade-in-d2">
      <button
        type="button"
        onClick={handleGoogleLogin}
        disabled={loading}
        className={
          'w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl ' +
          'bg-surface border-2 border-edge ' +
          'text-sm font-medium text-body ' +
          'hover:border-edge-input hover:shadow-md ' +
          'focus:outline-none focus:ring-2 focus:ring-amber-300 focus:ring-offset-2 ' +
          'disabled:opacity-50 disabled:cursor-not-allowed ' +
          'transform hover:scale-[1.01] active:scale-[0.99] transition-all duration-200'
        }
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
        Continue with Google
      </button>
    </div>
  );

  const divider = (text: string) => (
    <div className="relative my-6 login-fade-in-d2">
      <div className="absolute inset-0 flex items-center">
        <div className="w-full border-t border-amber-200/80" />
      </div>
      <div className="relative flex justify-center">
        <span className="px-3 text-xs text-amber-500/70 font-medium"
          style={{ background: 'linear-gradient(135deg, rgba(255,251,235,0.97), rgba(255,247,237,0.95))' }}
        >
          {text}
        </span>
      </div>
    </div>
  );

  const cardStyle = {
    background: 'linear-gradient(135deg, rgba(255,251,235,0.97) 0%, rgba(255,247,237,0.95) 100%)',
    backdropFilter: 'blur(20px)',
    boxShadow: '0 25px 60px rgba(0,0,0,0.3), 0 0 0 1px rgba(245,158,11,0.15)',
  };

  const spinner = (
    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
  );

  /* ---------------------------------------------------------------- */
  /*  Verify email view                                                */
  /* ---------------------------------------------------------------- */

  if (viewMode === 'verify') {
    return shell(
      <div className="rounded-2xl p-8 sm:p-10 login-fade-in" style={cardStyle}>
        {logoHeader('Check your email for a verification code')}

        <form className="space-y-4 login-fade-in-d3" onSubmit={handleVerify}>
          <div>
            <label htmlFor="verification-code" className="sr-only">Verification Code</label>
            <input
              id="verification-code"
              name="verification-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              className={inputClass}
              placeholder="6-digit verification code"
              maxLength={6}
            />
          </div>

          {errorBox}

          <button type="submit" disabled={loading} className={primaryBtnClass}>
            {loading ? (
              <span className="flex items-center justify-center gap-2">{spinner} Verifying...</span>
            ) : 'Verify Email'}
          </button>
        </form>

        <p className="text-center text-sm text-amber-700/60 mt-6 login-fade-in-d4">
          <button type="button" onClick={switchToLogin} className="font-medium text-amber-600 hover:text-amber-700 underline underline-offset-2">
            Back to sign in
          </button>
        </p>
      </div>
    );
  }

  /* ---------------------------------------------------------------- */
  /*  Sign up view                                                     */
  /* ---------------------------------------------------------------- */

  if (viewMode === 'signup') {
    return shell(
      <div className="rounded-2xl p-8 sm:p-10 login-fade-in" style={cardStyle}>
        {logoHeader('Create your account')}

        {googleButton}
        {divider('or sign up with email')}

        <form className="space-y-4 login-fade-in-d3" onSubmit={handleSignup}>
          <div>
            <label htmlFor="signup-email" className="sr-only">Email</label>
            <input
              id="signup-email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={signupEmail}
              onChange={(e) => setSignupEmail(e.target.value)}
              className={inputClass}
              placeholder="Email address"
            />
          </div>
          <div>
            <label htmlFor="signup-password" className="sr-only">Password</label>
            <input
              id="signup-password"
              name="password"
              type="password"
              autoComplete="new-password"
              required
              value={signupPassword}
              onChange={(e) => setSignupPassword(e.target.value)}
              className={inputClass}
              placeholder="Password (min 8 chars)"
              minLength={8}
            />
          </div>
          <div>
            <label htmlFor="signup-confirm-password" className="sr-only">Confirm Password</label>
            <input
              id="signup-confirm-password"
              name="confirm-password"
              type="password"
              autoComplete="new-password"
              required
              value={signupConfirmPassword}
              onChange={(e) => setSignupConfirmPassword(e.target.value)}
              className={inputClass}
              placeholder="Confirm password"
              minLength={8}
            />
          </div>

          {errorBox}

          <button type="submit" disabled={loading} className={primaryBtnClass}>
            {loading ? (
              <span className="flex items-center justify-center gap-2">{spinner} Creating account...</span>
            ) : 'Create Account'}
          </button>
        </form>

        <p className="text-center text-sm text-amber-700/60 mt-6 login-fade-in-d4">
          Already have an account?{' '}
          <button type="button" onClick={switchToLogin} className="font-medium text-amber-600 hover:text-amber-700 underline underline-offset-2">
            Sign in
          </button>
        </p>
      </div>
    );
  }

  /* ---------------------------------------------------------------- */
  /*  Main login view                                                  */
  /* ---------------------------------------------------------------- */

  return shell(
    <div className="rounded-2xl p-8 sm:p-10 login-fade-in" style={cardStyle}>
      {logoHeader('Track your plays, climb the rankings')}

      {googleButton}
      {divider('or sign in with email')}

      {/* login form */}
      <form className="space-y-4 login-fade-in-d3" onSubmit={handleLogin}>
        <div>
          <label htmlFor="username" className="sr-only">Username</label>
          <input
            id="username"
            name="username"
            type="text"
            autoComplete="username"
            required
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className={inputClass}
            placeholder="Email or username"
          />
        </div>
        <div>
          <label htmlFor="password" className="sr-only">Password</label>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputClass}
            placeholder="Password"
          />
        </div>

        {errorBox}

        <button type="submit" disabled={loading} className={primaryBtnClass}>
          {loading ? (
            <span className="flex items-center justify-center gap-2">{spinner} Signing in...</span>
          ) : 'Sign in'}
        </button>
      </form>

      <p className="text-center text-sm text-amber-700/60 mt-6 login-fade-in-d4">
        Don't have an account?{' '}
        <button type="button" onClick={switchToSignup} className="font-medium text-amber-600 hover:text-amber-700 underline underline-offset-2">
          Sign up
        </button>
      </p>
    </div>
  );
};

export default LoginPage;
